import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { safeStorage } from '@/lib/menuMemory';
import { createMenuGroupStore, groupOpenIn } from '@/lib/menuGroupStore';

/**
 * The open and closed groups of the menu (Warren, 28 October 2026), shared by the desktop top bar and the phone
 * drawer so the two always agree. A click flips one group and leaves the others alone; the state is remembered for
 * this user (lib/menuMemory.ts), and the group holding the page shown is open while you are on it.
 *
 * One store for the whole app (lib/menuGroupStore.ts), per user: both renderers are mounted at once, so a click in
 * one, including closing the group of the page you are on, shows in the other. Another tab's clicks arrive through
 * the storage event, and a click here never writes back over them.
 */
const store = createMenuGroupStore(safeStorage);

// One storage listener for every renderer; a storage event fires only in the other tabs of this origin.
let subscribers = 0;
function onStorage(e: StorageEvent) {
  try { if (e.storageArea !== window.localStorage) return; } catch { return; }
  store.storageChanged(e.key, e.newValue);
}
function subscribe(listener: () => void): () => void {
  const unsubscribe = store.subscribe(listener);
  if (subscribers++ === 0) window.addEventListener('storage', onStorage);
  return () => {
    unsubscribe();
    if (--subscribers === 0) window.removeEventListener('storage', onStorage);
  };
}

export function useMenuGroups(userId: string | null | undefined, currentPath: string) {
  const id = userId ?? '';
  const state = useSyncExternalStore(subscribe, () => store.get(id), () => store.get(id));
  // Groups closed by a click while on the page inside them are forgotten once the page changes, so the next page
  // inside a group opens it again. Until then they apply only to the page they were closed on.
  useEffect(() => { store.visit(id, currentPath); }, [id, currentPath]);

  const isOpen = useCallback(
    (key: string, holdsCurrentPage: boolean) => groupOpenIn(state, key, holdsCurrentPage, currentPath),
    [state, currentPath],
  );
  const toggle = useCallback(
    (key: string, holdsCurrentPage: boolean) => store.toggle(id, key, holdsCurrentPage, currentPath),
    [id, currentPath],
  );

  return { isOpen, toggle };
}
